import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Users, Watch, Tag, FolderTree, Plus, ShoppingCart, Layers } from 'lucide-react';
import { statsAPI, brandsAPI, categoriesAPI } from '../utils/api';
import { useCurrency } from '../contexts/CurrencyContext';

// Extracted constants to avoid inline object recreation on each render
const CHART_TICK = { fill: '#a1a1a1', fontSize: 11 };
const TOOLTIP_STYLE = { background: '#141519', border: '1px solid #2b2c2f', color: '#D0B36B', fontSize: 12, borderRadius: 8 };

const formatCurrency = (v) => {  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(0)}k`;
  return `$${v}`;
};

const KPI = ({ icon: Icon, label, value, onClick }) => (
  <Card className="cursor-pointer transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5" onClick={onClick}>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const QuickAction = ({ icon: Icon, title, desc, onClick }) => (
  <Card className="cursor-pointer transition-all hover:border-primary/30 hover:shadow-md" onClick={onClick}>
    <CardContent className="pt-5 pb-4 flex items-start gap-3">
      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div>
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { format: formatCurrency, formatFull, symbol: currSymbol } = useCurrency();
  const [stats, setStats] = useState({ customers: 0, listings: 0, brands: 0, categories: 0, totalRevenue: 0 });
  const [revenue, setRevenue] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [overview, rev, brands, cats] = await Promise.all([
          statsAPI.getOverview(), statsAPI.getRevenue(), brandsAPI.getAll(), categoriesAPI.getAll(),
        ]);
        setStats({ customers: overview.customers, listings: overview.listings, brands: brands.length, categories: cats.length, totalRevenue: overview.totalRevenue || 0 });
        setRevenue(rev);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-muted-foreground animate-pulse">Loading dashboard...</div></div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1 text-sm">Welcome to V Watch administration</p>
      </div>

      {/* 4 KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Customers" value={stats.customers} onClick={() => navigate('/users')} />
        <KPI icon={Watch} label="Listings" value={stats.listings} onClick={() => navigate('/watches')} />
        <KPI icon={Tag} label="Brands" value={stats.brands} onClick={() => navigate('/brands')} />
        <KPI icon={FolderTree} label="Categories" value={stats.categories} onClick={() => navigate('/categories')} />
      </div>

      {/* Revenue Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm sm:text-base">Revenue</CardTitle>
          <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg">
            <span className="text-lg font-bold text-primary">{currSymbol}</span>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Revenue</p>
              <p className="text-xl font-bold text-primary">{formatCurrency(stats.totalRevenue)}</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="h-64">
          {revenue.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No revenue data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenue}>
                <defs>
                  <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#D0B36B" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#D0B36B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#2b2c2f" strokeOpacity={0.5} />
                <XAxis dataKey="month" stroke="#6b7280" tick={CHART_TICK} />
                <YAxis stroke="#6b7280" tick={CHART_TICK} tickFormatter={(v) => formatCurrency(v)} />
                <Tooltip contentStyle={TOOLTIP_STYLE}
                  formatter={(v) => [formatCurrency(v), 'Revenue']} />
                <Area type="monotone" dataKey="revenue" stroke="#D0B36B" strokeWidth={2} fill="url(#goldGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <QuickAction icon={Plus} title="Add a new watch" desc="List a timepiece in your catalog" onClick={() => navigate('/watches/new')} />
          <QuickAction icon={ShoppingCart} title="Track open orders" desc="View and manage pending orders" onClick={() => navigate('/orders')} />
          <QuickAction icon={Layers} title="Curate categories" desc="Organize your catalog taxonomy" onClick={() => navigate('/categories')} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
